import { onValue, orderByChild, query, ref, startAt, type DataSnapshot } from 'firebase/database';
import type { PublicUser } from '@sirocco/shared';
import { db } from './app';
import { requireUid } from './auth';
import { listUnseen } from './blocks';
import { paths } from './paths';
import { toPublicUser, type UserRecord } from './records';
import { readList, readMany, readOptionalValue, writeValue } from './rtdb';
import { t } from '../i18n';

/**
 * Ce qui est arrive a ce que j'ai publie.
 *
 * **Une notification et un registre ne sont pas la meme chose.** La
 * premiere passe et s'oublie ; le second garde. C'est pourquoi les
 * fonctions ecrivent ici meme quand aucune notification ne part —
 * reglage coupe, aucun appareil enregistre, membre absent depuis un
 * mois. La cloche doit pouvoir dire ce qui s'est passe pendant qu'on
 * n'etait pas la.
 *
 * Les lignes sont ecrites par les Cloud Functions et **nul client ne les
 * ecrit** : ce serait sinon un moyen de se fabriquer une reputation, ou
 * d'en inventer une a quelqu'un d'autre.
 */

/** Ce qu'une ligne du registre raconte. */
export type GenreActivite = 'like' | 'commentaire' | 'mention' | 'signet';

interface LigneBrute {
  /* Le registre garde des lignes de trophees : ils n'ont plus d'ecran, on les passe. */
  type?: GenreActivite | 'trophee';
  fromId?: string;
  fromName?: string;
  postId?: string;
  commentId?: string;
  at?: number;
}

export interface Activite {
  id: string;
  genre: GenreActivite;
  /** Qui l'a fait, tel qu'on peut l'afficher. */
  auteur: PublicUser | null;
  /** Le nom retenu a l'instant du fait : un membre a pu se renommer depuis. */
  nom: string;
  postId: string | null;
  /** La reponse annoncee, pour ouvrir le post descendu jusqu'a elle. */
  commentId: string | null;
  quand: number;
  /** Arrivee depuis la derniere ouverture de la cloche. */
  neuve: boolean;
}

/**
 * Combien de lignes on garde a l'ecran.
 *
 * Une cloche n'est pas une archive : au-dela, on ne fait plus defiler, on
 * fouille. Et le volume lu suit ce nombre, pas la taille du registre.
 */
const LIGNES = 40;

/** Les dernieres lignes, la plus recente en tete. */
export async function listActivite(): Promise<Activite[]> {
  const uid = requireUid();

  const [brutes, vue, ecartes] = await Promise.all([
    readList<LigneBrute>(paths.activite(uid), { orderBy: 'at', limit: LIGNES }),
    readOptionalValue<number>(paths.activiteVue(uid)),
    listUnseen(),
  ]);

  const depuis = vue ?? 0;
  /*
    La plus recente en tete : la base les rend dans l'ordre du temps. Les
    lignes au sujet d'un compte bloque ou masque sautent — le serveur n'en
    ecrit plus, mais celles d'avant le geste sont encore dans le registre.
  */
  const sansEux = new Set(ecartes);
  const lignes = [...brutes]
    .reverse()
    .filter((ligne) => !ligne.fromId || ligne.fromId === uid || !sansEux.has(ligne.fromId));

  /*
    Les visages, en une passe.

    Le pseudo voyage avec la ligne — un registre se lit d'un bloc — mais
    pas la photo, qui change trop souvent pour etre copiee. On lit donc
    les fiches des seules personnes citees, une fois chacune.
  */
  const ids = [...new Set(lignes.map((ligne) => ligne.fromId).filter(Boolean))] as string[];
  const fiches = await readMany<UserRecord>(paths.user, ids);

  return lignes.flatMap((ligne) => {
    if (!ligne.type || ligne.type === 'trophee' || !ligne.at) return [];
    const fiche = ligne.fromId ? fiches.get(ligne.fromId) : undefined;
    return [
      {
        id: ligne.id,
        genre: ligne.type,
        auteur: fiche && ligne.fromId ? toPublicUser(ligne.fromId, fiche) : null,
        nom: ligne.fromName || t.commun.quelquun,
        postId: ligne.postId ?? null,
        commentId: ligne.commentId ?? null,
        quand: ligne.at,
        neuve: ligne.at > depuis,
      },
    ];
  });
}

/**
 * La cloche a ete ouverte : tout ce qui precede est vu.
 *
 * Le repere est une date et non un drapeau par ligne : une seule ecriture
 * suffit a eteindre la pastille, quel que soit le nombre de lignes.
 */
export async function marquerActiviteVue(): Promise<void> {
  await writeValue(paths.activiteVue(requireUid()), Date.now());
}

/**
 * Combien de lignes depuis la derniere ouverture de la cloche.
 *
 * La pastille portait un simple point, donc une seule ecoute bornee a la
 * derniere ligne suffisait. Elle porte un nombre : il faut les compter.
 *
 * On ne telecharge pas le registre pour autant — la requete part de la
 * date de lecture (`startAt`) et ne ramene donc que ce qui est neuf.
 * L'index `at` existe deja pour la liste. Quand la date de lecture
 * change, la requete se refait : c'est ce qui eteint la pastille a
 * l'ouverture.
 *
 * Les lignes au sujet d'un compte bloque ou masque ne comptent pas : la
 * pastille annoncerait sinon des nouvelles que la cloche ne montre plus.
 * Les deux listes s'ecoutent, pour que le nombre tombe au moment du geste
 * et non au prochain lancement.
 */
export function watchActiviteNeuves(onChange: (nombre: number) => void): () => void {
  const uid = requireUid();
  let arretLignes: (() => void) | null = null;
  let neuves: DataSnapshot | null = null;
  const ecartes = { bloques: new Set<string>(), masques: new Set<string>() };

  const compter = () => {
    let nombre = 0;
    neuves?.forEach((ligne) => {
      const brute = ligne.val() as LigneBrute | null;
      if (brute?.type === 'trophee') return;
      const de = brute?.fromId;
      if (!de || (!ecartes.bloques.has(de) && !ecartes.masques.has(de))) nombre += 1;
    });
    onChange(nombre);
  };

  // Une liste illisible ne doit pas eteindre la pastille : on compte sans elle.
  const ecouter = (chemin: string, cle: keyof typeof ecartes) =>
    onValue(
      ref(db(), chemin),
      (snap) => {
        ecartes[cle] = new Set(Object.keys((snap.val() as Record<string, true> | null) ?? {}));
        compter();
      },
      () => undefined,
    );
  const arretBloques = ecouter(paths.blockedUsers(uid), 'bloques');
  const arretMasques = ecouter(paths.hiddenUsers(uid), 'masques');

  const arretVue = onValue(ref(db(), paths.activiteVue(uid)), (snap) => {
    const vue = (snap.val() as number | null) ?? 0;
    arretLignes?.();
    arretLignes = onValue(
      query(ref(db(), paths.activite(uid)), orderByChild('at'), startAt(vue + 1)),
      (lignes) => {
        neuves = lignes;
        compter();
      },
      () => {
        neuves = null;
        onChange(0);
      },
    );
  });

  return () => {
    arretLignes?.();
    arretVue();
    arretBloques();
    arretMasques();
  };
}
